import React, { useState, useEffect } from 'react';
import { Cog6ToothIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import { GripVertical, Loader2 } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DropdownCustom from '../moduleComponents/dropdown_custom';
import api from '../../../connections/api';
import routes_api from '../../../connections/routes_api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../connections/AuthContext';
import { useResponsiveDrag } from 'services/useResponsiveDrag';
import McpKeysSection from './mcp_keys_section';

function AppVisibilityToggle({ pref, onToggleVisibility }) {
  return (
    <button
      type="button"
      onClick={() => onToggleVisibility(pref.app_key)}
      className="rounded bg-gray-200 p-1 hover:bg-gray-300"
      aria-label={pref.app_visible ? `Hide ${pref.app_key}` : `Show ${pref.app_key}`}
    >
      {pref.app_visible ? <EyeIcon className="h-5 w-5" /> : <EyeSlashIcon className="h-5 w-5" />}
    </button>
  );
}

function PinnedAppRow({ pref, onToggleVisibility }) {
  return (
    <div className="flex items-center justify-between border-b py-2">
      <div className="flex min-w-0 items-center gap-2">
        <span className="inline-flex h-6 w-6 shrink-0" aria-hidden="true" />
        <span className="font-medium">{pref.app_key}</span>
      </div>
      <AppVisibilityToggle pref={pref} onToggleVisibility={onToggleVisibility} />
    </div>
  );
}

function SortableAppRow({ pref, onToggleVisibility }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: pref.app_key,
  });
  const { dragHandleClass, dragHandleStyle } = useResponsiveDrag();

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center justify-between border-b py-2 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          className={dragHandleClass}
          style={dragHandleStyle}
          aria-label={`Reorder ${pref.app_key}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-gray-400" strokeWidth={2} />
        </button>
        <span className="font-medium">{pref.app_key}</span>
      </div>
      <AppVisibilityToggle pref={pref} onToggleVisibility={onToggleVisibility} />
    </div>
  );
}

// ##### SETTINGS TAB COMPONENT
const TabSettings = () => {
  const { user, get_user } = useAuth();
  const preferences = user?.app_preferences || [];
  const [localPreferences, setLocalPreferences] = useState([]);
  const [saving, setSaving] = useState(false);
  const [appInitial, setAppInitial] = useState(user?.app_initial || 'accounts');

  // Sync local state when user data changes
  useEffect(() => {
    if (preferences.length > 0) {
      setLocalPreferences([...preferences]);
    }
  }, [preferences]);

  // Update appInitial when user changes
  useEffect(() => {
    if (user?.app_initial) {
      setAppInitial(user.app_initial);
    }
  }, [user]);

  const {
    activationDistance,
    activationDelay,
    activationTolerance,
    enableTouchGestures,
  } = useResponsiveDrag();
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: activationDistance },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: activationDelay, tolerance: activationTolerance },
  });
  const sensors = useSensors(
    pointerSensor,
    ...(enableTouchGestures ? [touchSensor] : [])
  );

  const toggleVisibility = (key) => {
    setLocalPreferences((prev) =>
      prev.map((p) =>
        p.app_key === key ? { ...p, app_visible: !p.app_visible } : p
      )
    );
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setLocalPreferences((prev) => {
      const sorted = [...prev].sort((a, b) => a.app_order - b.app_order);
      const enabledSlots = [];
      const enabled = [];
      sorted.forEach((pref, index) => {
        if (pref.app_enabled && pref.app_key !== 'identity') {
          enabledSlots.push(index);
          enabled.push(pref);
        }
      });

      const oldIndex = enabled.findIndex((pref) => pref.app_key === active.id);
      const newIndex = enabled.findIndex((pref) => pref.app_key === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;

      const reordered = arrayMove(enabled, oldIndex, newIndex);
      const next = [...sorted];
      enabledSlots.forEach((slot, index) => {
        next[slot] = reordered[index];
      });
      return next.map((pref, index) => ({ ...pref, app_order: index + 1 }));
    });
  };

  const preferenceSignature = (prefs) =>
    [...prefs]
      .sort((a, b) => a.app_order - b.app_order)
      .map((pref) => `${pref.app_key}:${pref.app_visible ? 1 : 0}`)
      .join('|');
  const savedInitial = user?.app_initial || 'accounts';
  const hasChanges =
    localPreferences.length > 0 &&
    (appInitial !== savedInitial ||
      preferenceSignature(localPreferences) !== preferenceSignature(preferences));

  const handleCancel = () => {
    if (saving) return;
    setLocalPreferences(preferences.map((pref) => ({ ...pref })));
    setAppInitial(savedInitial);
  };

  const handleSave = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);
    try {
      // Save preferences
      const prefsPayload = localPreferences.reduce((acc, pref) => {
        acc[pref.app_key] = {
          app_visible: pref.app_visible,
          app_order: pref.app_order,
        };
        return acc;
      }, {});
      await api.patch(routes_api.accounts_userpreferences, prefsPayload);

      // Save app_initial if changed
      if (appInitial !== user?.app_initial) {
        await api.patch(routes_api.accounts_update, { app_initial: appInitial });
      }

      // Refresh user data to update Context
      await get_user();

      toast.success('Settings saved successfully.');
    } catch (error) {
      console.error('Error saving settings', error);
      toast.error('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  // Show loading state while user data is being fetched
  if (!user) {
    return (
      <div className="bg-white p-4 rounded-xl shadow text-gray-700">
        <div className="flex items-center justify-center py-8">
          <div className="border-4 border-gray-300 border-t-gray-700 rounded-full w-8 h-8 animate-spin"></div>
        </div>
      </div>
    );
  }

  const enabledApps = localPreferences
    .filter((pref) => pref.app_enabled)
    .sort((a, b) => a.app_order - b.app_order);
  const pinnedIdentity = enabledApps.find((pref) => pref.app_key === 'identity');
  const sortableApps = enabledApps.filter((pref) => pref.app_key !== 'identity');
  const enabledModules = enabledApps.filter(
    (pref) => pref.app_visible && pref.app_key !== 'accounts'
  );

  return (
    <div className="bg-white p-4 rounded-xl shadow text-gray-700">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Cog6ToothIcon className="w-5 h-5" />
        Account Basics
      </h2>

      <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2">
      {/* ##### APP VISIBILITY */}
      <div>
        <h3 className="mb-2 text-sm font-medium text-gray-700">Apps visibility</h3>
        {pinnedIdentity && (
          <PinnedAppRow pref={pinnedIdentity} onToggleVisibility={toggleVisibility} />
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={sortableApps.map((pref) => pref.app_key)}
            strategy={verticalListSortingStrategy}
          >
            {sortableApps.map((pref) => (
              <SortableAppRow
                key={pref.app_key}
                pref={pref}
                onToggleVisibility={toggleVisibility}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      {/* ##### INITIAL APP SELECTION */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          App on initialization
        </label>
        <DropdownCustom
          options={[
            { value: 'ecosystem', label: 'Default (Ecosystem)' },
            ...enabledModules
              .sort((a, b) => a.app_order - b.app_order)
              .map(pref => ({
                value: pref.app_key,
                label: pref.app_key.charAt(0).toUpperCase() + pref.app_key.slice(1)
              }))
          ]}
          selectedValue={appInitial}
          onSelect={setAppInitial}
          placeholder="Select Initial App"
          position="left"
          width="w-48"
          buttonClassName="py-2.5 bg-gray-50 hover:bg-gray-100"
          dropdownClassName="bg-gray-50"
          optionClassName="hover:bg-gray-100"
        />
      </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-module-accounts text-white rounded hover:bg-module-accounts/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} aria-hidden="true" />
              Saving
            </>
          ) : (
            'Save Changes'
          )}
        </button>
        {hasChanges && (
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="px-4 py-2 border rounded text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
        )}
      </div>

      <McpKeysSection />
    </div>
  );
};

export default TabSettings;